import type { SharedPlaceSettings } from './sharedPlaceSettings'

export type BillDayTab = 'active' | 'completed' | 'disputed'
export type DayPeriod = 'today' | 'yesterday' | 'past'

export interface BillDateSource {
  createdAt: string
  paidAt: string | null
  cancelledAt?: string | null
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Calendar date yyyy-MM-dd for an instant in the given IANA timezone. */
export function businessDateForInstant(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso))
  const year = parts.find((p) => p.type === 'year')?.value ?? '1970'
  const month = parts.find((p) => p.type === 'month')?.value ?? '01'
  const day = parts.find((p) => p.type === 'day')?.value ?? '01'
  return `${year}-${month}-${day}`
}

/** Add calendar days to a yyyy-MM-dd string (UTC date math). */
export function addCalendarDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + days))
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`
}

export function todayBusinessDate(timezone: string, nowIso = new Date().toISOString()): string {
  return businessDateForInstant(nowIso, timezone)
}

export function yesterdayBusinessDate(timezone: string, nowIso = new Date().toISOString()): string {
  return addCalendarDays(todayBusinessDate(timezone, nowIso), -1)
}

/** Monday–Sunday week that contains a yyyy-MM-dd business date. */
export function weekContainingBusinessDate(dateStr: string): { startDate: string; endDate: string } {
  const [y, m, d] = dateStr.split('-').map(Number)
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  const fromMonday = weekday === 0 ? 6 : weekday - 1
  const startDate = addCalendarDays(dateStr, -fromMonday)
  return { startDate, endDate: addCalendarDays(startDate, 6) }
}

/** Calendar parts of an instant in an IANA timezone. Hour is 0–23. */
export function businessWallClock(
  iso: string,
  timezone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(iso))
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0')
  let hour = num('hour')
  if (hour === 24) hour = 0
  return {
    year: num('year'),
    month: num('month'),
    day: num('day'),
    hour,
    minute: num('minute'),
    second: num('second'),
  }
}

/** `yyyy-MM-dd HH:mm` in the place timezone. */
export function formatBusinessDateTime(iso: string, timezone: string): string {
  const clock = businessWallClock(iso, timezone)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${clock.year}-${p(clock.month)}-${p(clock.day)} ${p(clock.hour)}:${p(clock.minute)}`
}

/** Which calendar date to bucket a bill under for a given tab in place timezone. */
export function billDateForBucket(
  bill: BillDateSource,
  tab: BillDayTab,
  timezone: string,
): string | null {
  if (tab === 'completed') {
    const iso = bill.paidAt || bill.cancelledAt || bill.createdAt
    return iso ? businessDateForInstant(iso, timezone) : null
  }
  return bill.createdAt ? businessDateForInstant(bill.createdAt, timezone) : null
}

export function classifyBillDay(billDate: string, todayDate: string): DayPeriod {
  if (billDate === todayDate) return 'today'
  const yesterday = addCalendarDays(todayDate, -1)
  if (billDate === yesterday) return 'yesterday'
  return 'past'
}

export type BillDateScope = { mode: 'day'; date: string } | { mode: 'toDate' }

export interface BillDateScopeMatchOptions {
  /** Active tab: keep unpaid/open bills from earlier days on the selected day. */
  includeOlderActive?: boolean
  isActive?: boolean
}

/** Whether a bill belongs in the current date scope for a tab's bucket date. */
export function billMatchesDateScope(
  bill: BillDateSource,
  scope: BillDateScope,
  tab: BillDayTab,
  timezone: string,
  opts?: BillDateScopeMatchOptions,
): boolean {
  if (scope.mode === 'toDate') return true
  const billDate = billDateForBucket(bill, tab, timezone)
  if (!billDate) return false
  if (billDate === scope.date) return true
  if (opts?.includeOlderActive && opts.isActive && billDate < scope.date) return true
  return false
}

/** Display a yyyy-MM-dd business date as dd/mm/yyyy. */
export function formatBusinessDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  if (!y || !m || !d) return dateStr
  return `${d}/${m}/${y}`
}

export function resolvePlaceTimezone(settings: Pick<SharedPlaceSettings, 'timezone'> | null | undefined): string {
  const tz = settings?.timezone?.trim()
  if (!tz) {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    } catch {
      return 'UTC'
    }
  }
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return tz
  } catch {
    return 'UTC'
  }
}

export function formatBusinessClock(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(iso))
}

/** Hour, minute, and second in the place timezone (no date). */
export function formatBusinessTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(iso))
}

export interface FormatPrintTimestampOptions {
  /** Append a short offset like `GMT+8`. Print invoices/reports only — not kitchen tickets or screens. */
  offset?: boolean
}

/** `GMT+08:00` / `GMT+08` → `GMT+8`; keep nonzero minutes (`GMT-5:30`). */
export function formatGmtOffset(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  }).formatToParts(new Date(iso))
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value?.trim() ?? 'GMT'
  const match = raw.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::?(\d{2}))?$/i)
  if (!match) return raw.replace(/^UTC$/i, 'GMT')
  const hours = String(Number(match[2]))
  const minutes = match[3] && match[3] !== '00' ? `:${match[3]}` : ''
  return `GMT${match[1]}${hours}${minutes}`
}

/** Compact, locale-pinned timestamp for printed output and matching POS screens. */
export function formatPrintTimestamp(
  iso: string,
  timezone: string,
  options?: FormatPrintTimestampOptions,
): string {
  const stamp = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso))
  return options?.offset ? `${stamp} ${formatGmtOffset(iso, timezone)}` : stamp
}

/** Date only, for invoice Date fields. */
export function formatPrintDate(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso))
}

/** Hour and minute in the place timezone (24h, pinned locale) for order-detail headers. */
export function formatPrintTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso))
}

/** Common IANA timezones for settings picker (not exhaustive). */
export const COMMON_TIMEZONES: string[] = [
  'Asia/Jakarta',
  'Asia/Singapore',
  'Asia/Bangkok',
  'Asia/Manila',
  'Asia/Kuala_Lumpur',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Australia/Sydney',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
]
